import type { CustomActionHandler, CustomActionMap, CustomActionResponse } from "../types";
import { registerTools } from "../api/chatbase-api";

/**
 * Wraps each handler so that unhandled rejections are caught and returned
 * as a properly formatted error response instead of crashing silently.
 */
function wrapHandler(
  name: string,
  handler: CustomActionHandler,
  debug: boolean,
): CustomActionHandler {
  return async (args, user): Promise<CustomActionResponse> => {
    try {
      if (debug) {
        console.debug(`[chatbase] action "${name}" invoked`, { args, user });
      }
      return await handler(args, user);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      console.error(`[chatbase] action "${name}" threw:`, err);
      return { status: "error", error: message };
    }
  };
}

/**
 * Registers client-side custom actions with Chatbase.
 *
 * All handlers are wrapped with error handling so a thrown exception never
 * leaves the action unresolved.
 */
export function registerCustomActions(actions: CustomActionMap, debug = false): void {
  const wrapped: CustomActionMap = {};
  for (const [name, handler] of Object.entries(actions)) {
    wrapped[name] = wrapHandler(name, handler, debug);
  }
  registerTools(wrapped);
}
